import React, { useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Tag,
  Avatar,
  Typography,
  Card,
  Select,
  Input,
  Dropdown,
  MenuProps,
  Tooltip,
} from "antd";
import { useList, useDelete, useNavigation } from "@refinedev/core";
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Content, ContentFilters } from "@/types";
import { apiService } from "@/services/api";
import { EtiquetteClassification } from "@/components/content/SelecteurClassification";
import { ProcessingStatusBadge } from "@/components/media/ProcessingStatusBadge";

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Met en forme une date, ou rend un tiret si elle est absente ou illisible.
 *
 * La colonne affichait « Invalid Date » : `new Date(undefined)` produit une date
 * invalide, que `toLocaleDateString` rend litteralement. Les contenus crees
 * avant l'ajout du champ n'ont pas de date d'ajout — il n'en existe aucune
 * trace, et un tiret le dit honnetement.
 */
function formaterDate(valeur?: string | null): string {
  if (!valeur) return "—";
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fr-FR");
}

// Meme vocabulaire que ProcessingStatusBadge : l'etape dit ou en est le
// transcodage, ce qu'un pourcentage seul ne raconte pas. Un contenu bloque en
// « Telechargement » pendant une heure n'a pas le meme sens qu'a 40 % de
// transcodage.
/**
 * Barre horizontale qui passe a la ligne.
 *
 * `Row`/`Col flex="auto"` et `Space` sans `wrap` maintiennent tout sur une
 * seule ligne : avec un nombre de types variable, la page debordait.
 */
const barreFlexible: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 16,
  alignItems: "center",
  justifyContent: "space-between",
};

const etiquettesEtape: Record<string, string> = {
  DOWNLOADING: "Telechargement",
  TRANSCODING: "Transcodage",
  UPLOADING: "Upload HLS",
};

export const ContentList: React.FC = () => {
  const [filters, setFilters] = useState<ContentFilters>({});
  const [searchValue, setSearchValue] = useState("");
  // Onglet actif. « all », « drafts », ou l'identifiant d'un type reel.
  const [contentType, setContentType] = useState<string>("all");

  // Types et genres viennent de l'API : les options etaient ecrites en dur et
  // ne correspondaient a aucun identifiant reel.
  const [typesDisponibles, setTypesDisponibles] = useState<
    Array<{ id: number; title: string }>
  >([]);
  const [genresDisponibles, setGenresDisponibles] = useState<
    Array<{ id: number; title: string }>
  >([]);

  useEffect(() => {
    (async () => {
      try {
        const [types, genres] = await Promise.all([
          apiService.getContentTypes(),
          apiService.getGenres(),
        ]);
        setTypesDisponibles(types ?? []);
        setGenresDisponibles(genres ?? []);
      } catch (e) {
        console.error("Chargement des types et genres impossible", e);
      }
    })();
  }, []);

  // Un onglet agit sur un filtre reel : un type porte sur `type`, les
  // brouillons sur `status`. Les deux s'excluent, d'ou la remise a zero.
  const choisirOnglet = (onglet: string) => {
    setContentType(onglet);
    if (onglet === "all") {
      setFilters({ ...filters, type: undefined, status: undefined });
    } else if (onglet === "drafts") {
      setFilters({ ...filters, type: undefined, status: "DRAFT" });
    } else {
      setFilters({ ...filters, type: Number(onglet), status: undefined });
    }
  };

  // Nombre de vues par contenu. La colonne existait deja mais lisait un champ
  // 'viewCount' que l'API ne renvoie pas : elle affichait toujours 0.
  const [viewsByContent, setViewsByContent] = useState<Record<number, number>>({});

  useEffect(() => {
    (async () => {
      try {
        const stats = await apiService.getViewStats();
        const map: Record<number, number> = {};
        for (const stat of stats) {
          // Spectateurs distincts, et non nombre d'evenements PLAY : un meme
          // abonne relançant la lecture dix fois comptait pour dix vues. La
          // colonne affichait ainsi 22 vues pour un film regarde par une seule
          // personne.
          map[stat.contentId] = stat.uniqueViewers;
        }
        setViewsByContent(map);
      } catch {
        // Statistiques indisponibles : la liste reste utilisable, les vues
        // s'affichent a 0.
      }
    })();
  }, []);

  const { mutate: deleteContent } = useDelete();
  const { push } = useNavigation();

  const {
    data: contentsData,
    isLoading,
    refetch,
    error,
  } = useList<Content>({
    resource: "contents",
    pagination: { current: 1, pageSize: 20 },
    filters: [
      ...(searchValue
        ? [
            {
              field: "search",
              operator: "contains" as const,
              value: searchValue,
            },
          ]
        : []),
      ...(filters.type
        ? [{ field: "type", operator: "eq" as const, value: filters.type }]
        : []),
      ...(filters.genre
        ? [{ field: "genre", operator: "eq" as const, value: filters.genre }]
        : []),
      ...(filters.status
        ? [{ field: "status", operator: "eq" as const, value: filters.status }]
        : []),
    ].filter(Boolean),
  });

  // Un transcodage dure des heures. Sans rafraichissement, la liste montrait
  // un etat fige et l'on ne pouvait pas distinguer « en cours » de « bloque » :
  // c'est la source des confusions repetees sur l'avancement.
  const transcodageEnCours = (contentsData?.data ?? []).some(
    (c) =>
      c.videoProcessingStatus === "PROCESSING" ||
      c.videoProcessingStatus === "PENDING" ||
      c.trailerProcessingStatus === "PROCESSING" ||
      c.trailerProcessingStatus === "PENDING"
  );

  React.useEffect(() => {
    // On n'interroge le serveur que lorsqu'il y a quelque chose a suivre.
    if (!transcodageEnCours) return;
    const minuteur = window.setInterval(() => refetch(), 10000);
    return () => window.clearInterval(minuteur);
  }, [transcodageEnCours, refetch]);

  // Logs de débogage
  React.useEffect(() => {
    console.log("ContentList - Données reçues:", contentsData);
    console.log("ContentList - État de chargement:", isLoading);
    console.log("ContentList - Erreur:", error);
  }, [contentsData, isLoading, error]);

  const handleDelete = (id: number) => {
    deleteContent({
      resource: "contents",
      id,
      successNotification: () => ({
        message: "Contenu supprimé",
        description: "Le contenu a été supprimé avec succès",
        type: "success",
      }),
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "green";
      case "DRAFT":
        return "blue";
      case "PENDING":
        return "orange";
      case "ARCHIVED":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return <PlayCircleOutlined />;
      case "DRAFT":
        return <EditOutlined />;
      case "PENDING":
        return <CalendarOutlined />;
      case "ARCHIVED":
        return <EyeInvisibleOutlined />;
      default:
        return null;
    }
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return "N/A";
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return `${hours}h ${minutes}min`;
  };

  const formatViews = (views?: number) => {
    if (!views) return "0";
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  const getActionMenu = (record: Content): MenuProps["items"] => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "Voir",
      onClick: () => push(`/content/show/${record.id}`),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Modifier",
      onClick: () => push(`/content/edit/${record.id}`),
    },
    {
      type: "divider",
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Supprimer",
      danger: true,
      onClick: () => handleDelete(record.id),
    },
  ];

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      width: 50,
      render: () => <input type="checkbox" />,
    },
    {
      title: "CONTENU",
      dataIndex: "content",
      key: "content",
      render: (_: any, record: Content) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size={60}
            src={record.posterUrl || record.poster_path}
            style={{ borderRadius: 8 }}
          >
            <PlayCircleOutlined />
          </Avatar>
          <div>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              {record.title}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDuration(record.videoDuration)} •{" "}
              {new Date(record.release_date).getFullYear()}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      render: (type: any) => <Tag color="blue">{type?.title || "Film"}</Tag>,
    },
    {
      title: "GENRE",
      dataIndex: "genres",
      key: "genres",
      render: (genres: any[]) => (
        <Space wrap>
          {genres?.slice(0, 2).map((genre, index) => (
            <Tag key={index} color="purple">
              {genre.title}
            </Tag>
          ))}
          {genres?.length > 2 && (
            <Tag color="default">+{genres.length - 2}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "STATUT",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      // Colonne ajoutée avec CMP-03 : d'un coup d'œil, on voit ce qui reste
      // « Non classé » — et c'est précisément ce qui bloque la fiche des
      // magasins et le futur profil enfant.
      title: "ÂGE",
      key: "ageRating",
      width: 90,
      render: (_: any, record: Content) => (
        <EtiquetteClassification
          age={record.ageRating}
          descripteurs={record.contentDescriptors}
        />
      ),
    },
    {
      title: "VIDEO",
      key: "videoStatus",
      width: 120,
      render: (_: any, record: Content) => (
        <Space direction="vertical" size={2}>
          <ProcessingStatusBadge
            status={record.videoProcessingStatus}
            progress={record.videoProcessingProgress ?? null}
            stage={record.videoProcessingStage ?? null}
            error={record.videoProcessingError ?? null}
            compact
          />
          {record.videoProcessingStage && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {etiquettesEtape[record.videoProcessingStage] ??
                record.videoProcessingStage}
            </Text>
          )}
          {record.trailerProcessingStatus && (
            <ProcessingStatusBadge
              status={record.trailerProcessingStatus}
              progress={record.trailerProcessingProgress ?? null}
              stage={record.trailerProcessingStage ?? null}
              error={record.trailerProcessingError ?? null}
              type="trailer"
              compact
            />
          )}
        </Space>
      ),
    },
    {
      title: "SPECTATEURS",
      dataIndex: "viewCount",
      key: "viewCount",
      render: (_: number, record: Content) => (
        <Text strong>{formatViews(viewsByContent[Number(record.id)] ?? 0)}</Text>
      ),
    },
    {
      title: "DATE D'AJOUT",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <Text type="secondary">{formaterDate(date)}</Text>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      render: (_: any, record: Content) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => push(`/content/show/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => push(`/content/edit/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenu(record) }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Gestion du Contenu
        </Title>
        <Text type="secondary">Gérez vos films, séries et vidéos</Text>
      </div>

      {/* Onglets de type. Leur nombre depend des types en base : la barre doit
          donc pouvoir passer a la ligne, sinon elle deborde la page. */}
      <Card style={{ marginBottom: 24 }}>
        <div style={barreFlexible}>
          <Space wrap>
              <Button
                type={contentType === "all" ? "primary" : "default"}
                onClick={() => choisirOnglet("all")}
              >
                Tous les contenus
              </Button>
              {typesDisponibles.map((t) => (
                <Button
                  key={t.id}
                  type={contentType === String(t.id) ? "primary" : "default"}
                  onClick={() => choisirOnglet(String(t.id))}
                >
                  {t.title}
                </Button>
              ))}
              <Button
                type={contentType === "drafts" ? "primary" : "default"}
                onClick={() => choisirOnglet("drafts")}
              >
                Brouillons
              </Button>
          </Space>
          {/* Le bouton « Filtres » a ete retire : il n'avait aucun
              gestionnaire. Voir BO-11 au backlog. */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => push(`/content/create`)}
          >
            Ajouter un contenu
          </Button>
        </div>
      </Card>

      {/* Recherche et filtres, sur une barre qui passe a la ligne plutot que
          de deborder quand la fenetre est etroite. */}
      <Card style={{ marginBottom: 24 }}>
        <div style={barreFlexible}>
            <Input
              placeholder="Titre, acteur, réalisateur..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ flex: "1 1 240px", minWidth: 200, maxWidth: 400 }}
            />
            <Space wrap>
              <Select
                placeholder="Genre"
                style={{ width: 170 }}
                allowClear
                value={filters.genre}
                onChange={(value) => setFilters({ ...filters, genre: value })}
              >
                {genresDisponibles.map((g) => (
                  <Option key={g.id} value={g.id}>
                    {g.title}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Statut"
                style={{ width: 150 }}
                allowClear
                value={filters.status}
                onChange={(value) => {
                  setFilters({ ...filters, status: value });
                  // L'onglet « Brouillons » agit sur le meme filtre : le garder
                  // actif alors qu'un autre statut est choisi serait trompeur.
                  if (value !== "DRAFT" && contentType === "drafts") {
                    setContentType("all");
                  }
                }}
              >
                <Option value="PUBLISHED">Publié</Option>
                <Option value="DRAFT">Brouillon</Option>
                <Option value="PENDING">En attente</Option>
                <Option value="ARCHIVED">Archivé</Option>
              </Select>
              {/* Le selecteur « Date d'ajout » a ete retire : il n'avait aucun
                  gestionnaire et l'API n'accepte pas d'intervalle de dates.
                  Voir BO-10 au backlog. */}
              <Text
                type="secondary"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setFilters({});
                  setSearchValue("");
                  setContentType("all");
                }}
              >
                Réinitialiser les filtres
              </Text>
            </Space>
        </div>
      </Card>

      {/* Content Table */}
      <Card>
        {error ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Text type="danger">
              Erreur lors du chargement des contenus:{" "}
              {error.message || "Erreur inconnue"}
            </Text>
            <br />
            <Button
              type="primary"
              onClick={() => refetch()}
              style={{ marginTop: 16 }}
            >
              Réessayer
            </Button>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={(contentsData?.data as Content[]) || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              total: contentsData?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} contenus`,
            }}
            locale={{
              emptyText: "Aucun contenu trouvé",
            }}
          />
        )}
      </Card>
    </div>
  );
};
